import { Dialog } from "@components/ui/Dialog";
import { useToast } from "@lib/hooks/useToast";
import {
  Download,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileType,
  Paperclip,
  Trash2,
  UploadCloud,
  X,
} from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { TICKET_ACCEPT_MIME, TICKET_MAX_FILE_SIZE } from "../../config/schemas";
import { downloadAttachment, useAttachmentBlob } from "../../hooks/useAttachmentBlob";
import { useTicketAttachments } from "../../hooks/useTicketAttachments";
import { useDeleteAttachment, useUploadTicketAttachment } from "../../hooks/useTicketMutations";
import type { TicketAttachment } from "../../types/ticket.types";
import { formatFileSize } from "../../utils";

interface TicketAttachmentsProps {
  ticketId: number | string;
  propAttachments: readonly TicketAttachment[];
}

function isImage(mimeType: string): boolean {
  return mimeType.startsWith("image/");
}

function extensionOf(filename: string): string {
  const parts = filename.split(".");
  return parts.length > 1 ? parts[parts.length - 1]!.toUpperCase() : "";
}

function fileTypeIcon(mimeType: string) {
  if (mimeType.startsWith("image/")) return FileImage;
  if (mimeType === "application/pdf") return FileType;
  if (mimeType === "text/csv" || mimeType.includes("spreadsheet")) return FileSpreadsheet;
  return FileText;
}

function AttachmentThumbnail({ attachment }: { attachment: TicketAttachment }) {
  const { objectUrl, isPending, isError } = useAttachmentBlob(attachment.id);
  const Icon = fileTypeIcon(attachment.mimeType);

  if (!isImage(attachment.mimeType)) {
    return (
      <div className="flex flex-col items-center justify-center gap-0.5">
        <Icon className="h-4 w-4 text-base-content/35" />
        <span className="text-[7px] font-mono font-bold tracking-wider text-base-content/35">
          {extensionOf(attachment.originalFilename)}
        </span>
      </div>
    );
  }

  if (isPending) {
    return <span className="loading loading-spinner loading-xs text-base-content/40" />;
  }

  if (isError || !objectUrl) {
    return <Icon className="h-4 w-4 text-base-content/35" />;
  }

  return (
    <img
      src={objectUrl}
      alt={attachment.originalFilename}
      className="h-full w-full object-cover"
      loading="lazy"
    />
  );
}

export function TicketAttachments({ ticketId, propAttachments }: TicketAttachmentsProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [preview, setPreview] = useState<TicketAttachment | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);

  const previewBlob = useAttachmentBlob(preview?.id ?? null);
  const toast = useToast();

  const deleteAttachment = useDeleteAttachment();
  const uploadAttachment = useUploadTicketAttachment();
  const { data: fetchedAttachments } = useTicketAttachments(ticketId);

  // Prefer the dedicated attachments endpoint; fall back to the embedded prop.
  const attachments = fetchedAttachments ?? propAttachments ?? [];

  const handleFiles = useCallback(
    (fileList: FileList) => {
      const fileErrors: string[] = [];
      const allowedMimes = TICKET_ACCEPT_MIME.split(",");

      for (const file of Array.from(fileList)) {
        if (file.size > TICKET_MAX_FILE_SIZE) {
          fileErrors.push(`${file.name} : trop volumineux (max 10 Mo)`);
          continue;
        }
        if (!allowedMimes.includes(file.type)) {
          fileErrors.push(`${file.name} : type non autorisé`);
          continue;
        }
        uploadAttachment.mutate({
          ticketId,
          file,
          onProgress: (percent) => setUploadProgress(percent),
        });
      }

      setLocalError(fileErrors.length > 0 ? fileErrors.join(" · ") : null);
    },
    [ticketId, uploadAttachment],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      if (e.dataTransfer.files.length > 0) {
        handleFiles(e.dataTransfer.files);
      }
    },
    [handleFiles],
  );

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        handleFiles(e.target.files);
        e.target.value = "";
      }
    },
    [handleFiles],
  );

  const handleDelete = (attachment: TicketAttachment) => {
    deleteAttachment.mutate({ id: attachment.id, ticketId });
  };

  return (
    <div className="card bg-base-200 border border-base-300/70 shadow-sm hover:shadow-md hover:border-primary/20 transition-all duration-200">
      <div className="card-body gap-4 p-4 sm:p-5">
        <div className="flex items-center justify-between">
          <h3 className="card-title text-sm font-semibold gap-2">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 text-primary ring-1 ring-primary/15">
              <Paperclip className="h-3.5 w-3.5" strokeWidth={2.25} />
            </span>
            Pièces jointes
          </h3>
          <span className="badge badge-ghost badge-sm font-mono">{attachments.length}</span>
        </div>

        {/* Attachment grid */}
        {attachments.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {attachments.map((attachment, idx) => (
              <div
                key={attachment.id}
                className="group relative flex items-center gap-3 rounded-lg border border-base-300/60 bg-base-200/40 p-2.5 transition-all duration-200 hover:border-primary/30 hover:bg-base-200/70 hover:shadow-sm animate-in fade-in slide-in-from-bottom-1 cursor-pointer"
                onClick={() => setPreview(attachment)}
                style={{
                  animationDelay: `${idx * 40}ms`,
                  animationDuration: "260ms",
                  animationFillMode: "backwards",
                }}
              >
                {/* Thumbnail */}
                <div className="h-11 w-11 shrink-0 overflow-hidden rounded-md bg-base-300/60 ring-1 ring-base-300/50 flex items-center justify-center">
                  <AttachmentThumbnail attachment={attachment} />
                </div>

                {/* Info */}
                <div className="min-w-0 flex-1">
                  <p
                    className="truncate text-sm font-medium text-base-content/90"
                    title={attachment.originalFilename}
                  >
                    {attachment.originalFilename}
                  </p>
                  <p className="font-mono text-[11px] text-base-content/40">
                    {formatFileSize(attachment.fileSize)}
                  </p>
                </div>

                {/* Actions — revealed on hover, always visible on touch */}
                <div className="flex shrink-0 items-center gap-1 opacity-70 transition-opacity duration-150 group-hover:opacity-100">
                  <button
                    type="button"
                    className="btn btn-ghost btn-xs btn-square"
                    title="Télécharger"
                    onClick={(e) => {
                      e.stopPropagation();
                      downloadAttachment(attachment).catch(() =>
                        toast.error("Erreur lors du téléchargement", {
                          dedupeKey: "download-attachment",
                        }),
                      );
                    }}
                  >
                    <Download className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-xs btn-square text-base-content/30 hover:text-error hover:bg-error/10"
                    title="Supprimer"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(attachment);
                    }}
                    disabled={deleteAttachment.isPending}
                  >
                    {deleteAttachment.isPending ? (
                      <span className="loading loading-spinner loading-xs" />
                    ) : (
                      <Trash2 className="h-3.5 w-3.5" />
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Upload zone */}
        <div
          className={`relative flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed p-6 cursor-pointer transition-all duration-200 ${
            isDragOver
              ? "border-primary bg-primary/[0.06] shadow-[0_0_0_4px_oklch(var(--p)_/_0.12)] scale-[1.01]"
              : "border-base-300 bg-base-200/30 hover:border-primary/40 hover:bg-primary/[0.03]"
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleClick}
          role="button"
          tabIndex={0}
        >
          <span
            className={`flex h-11 w-11 items-center justify-center rounded-full transition-all duration-200 ${
              isDragOver
                ? "bg-primary/15 text-primary scale-110"
                : "bg-base-300/50 text-base-content/35"
            }`}
          >
            <UploadCloud className="h-5 w-5" />
          </span>
          <p className="text-xs text-base-content/55">
            Glissez vos fichiers ici ou{" "}
            <span className="font-semibold text-primary underline decoration-primary/30 underline-offset-2">
              parcourir
            </span>
          </p>
          <p className="font-mono text-[10px] text-base-content/30">
            PNG · JPG · JPEG · GIF · WebP · PDF · TXT · CSV — max 10&nbsp;Mo/fichier
          </p>

          {uploadAttachment.isPending && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-xl bg-base-100/70 backdrop-blur-[1px] px-6">
              <progress
                className="progress progress-primary w-full max-w-xs"
                value={uploadProgress ?? 0}
                max={100}
              />
              <span className="font-mono text-[10px] text-base-content/50">
                {uploadProgress ?? 0}%
              </span>
            </div>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          multiple
          accept={TICKET_ACCEPT_MIME}
          onChange={handleInputChange}
        />

        {/* Error */}
        {localError && (
          <div className="flex items-start gap-2 rounded-lg bg-error/10 px-3 py-2 text-xs text-error animate-in fade-in slide-in-from-top-1 duration-200">
            <X className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>{localError}</span>
          </div>
        )}
      </div>

      {/* Preview modal */}
      <Dialog
        open={preview !== null}
        onClose={() => setPreview(null)}
        title={preview?.originalFilename}
        subtitle={preview ? formatFileSize(preview.fileSize) : undefined}
        icon={preview ? <Paperclip className="h-4 w-4" /> : undefined}
        size="lg"
        footer={
          preview ? (
            <>
              <button
                type="button"
                className="btn btn-primary btn-sm gap-1.5"
                onClick={() =>
                  downloadAttachment(preview).catch(() =>
                    toast.error("Erreur lors du téléchargement", {
                      dedupeKey: "download-attachment",
                    }),
                  )
                }
              >
                <Download className="h-3.5 w-3.5" />
                Télécharger
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setPreview(null)}
              >
                Fermer
              </button>
            </>
          ) : undefined
        }
      >
        {preview && (
          <div className="rounded-lg border border-base-300 bg-base-200/40 overflow-hidden">
            {previewBlob.isPending ? (
              <div className="flex h-[40vh] items-center justify-center">
                <span className="loading loading-spinner loading-md text-primary" />
              </div>
            ) : previewBlob.isError || !previewBlob.objectUrl ? (
              <div className="flex h-[40vh] items-center justify-center text-sm text-base-content/50">
                Impossible de charger l'aperçu
              </div>
            ) : isImage(preview.mimeType) ? (
              <img
                src={previewBlob.objectUrl}
                alt={preview.originalFilename}
                className="w-full h-auto"
              />
            ) : (
              <iframe
                src={previewBlob.objectUrl}
                title={preview.originalFilename}
                className="w-full h-[70vh]"
              />
            )}
          </div>
        )}
      </Dialog>
    </div>
  );
}
